import { useState, useEffect, useMemo, useCallback } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { motion } from 'framer-motion'
import {
  Target,
  TrendingUp,
  Award,
  Briefcase,
  Plus,
  CheckCircle2,
  Clock,
  XCircle,
  Sparkles,
  BookOpen,
  Users,
  AlertCircle,
  Calendar,
  Edit,
  Trash2,
  Check,
  Compass
} from 'lucide-react'
import { idpService, IDPItemStatus } from '@/services/api/idpService'
import type {
  IndividualDevelopmentPlan,
  IDPStrength,
  IDPDevelopmentArea,
  IDPIntervention,
  CareerAspiration,
  PerformanceCategory,
  DevelopmentFocus,
  CreateStrengthRequest,
  CreateDevelopmentAreaRequest,
  CreateInterventionRequest,
  CreateCareerAspirationRequest
} from '@/services/api/idpService'
import type { EmployeePerformancePlan } from '@/services/api/performanceService'

interface KPAOption {
  id: string | number
  name: string
  display_name?: string
  weight_percentage?: number
}
import toast from 'react-hot-toast'
import { StrengthDialog } from './StrengthDialog'
import { AddDevelopmentAreaDialog } from './AddDevelopmentAreaDialog'
import { AddInterventionDialog } from './AddInterventionDialog'
import { CareerGoalSection } from './CareerGoalSection'
import { LeaderGrowthFocus } from './LeaderGrowthFocus'
import { PerformanceHistoryTrend } from './PerformanceHistoryTrend'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { ManagerActionDialog } from './ManagerActionDialog'
import { useJourneyViewMode } from '@/lib/journeyViewMode'

// Status Badge Component.
// Detailed view keeps the full RAG/metric palette. Simple view (default)
// uses the calm palette: stone/off-white surfaces + heritage-blue accents,
// with emerald reserved for genuine achievement (Acknowledged / Completed).
/** What the Annual I&G Review writes onto a plan carries this in proposed_by. */
const REVIEW_PROPOSER = 'talent_review'

function StatusBadge({ status, proposedBy }: { status: IDPItemStatus; proposedBy?: string | null }) {
  const [viewMode] = useJourneyViewMode()
  const detailed = viewMode === 'detailed'

  // An item the review put here says so, whatever its status — it was the
  // room's decision, not something the People Leader typed in.
  if (proposedBy === REVIEW_PROPOSER) {
    return (
      <Badge className={`${detailed ? 'bg-blue-100 text-blue-800' : 'bg-heritage-blue/10 text-heritage-blue'} flex items-center gap-1`}>
        <Compass className="h-3 w-3" />
        From the Annual I&G Review
      </Badge>
    )
  }

  const detailedConfig = {
    AI_GENERATED: { color: 'bg-purple-100 text-purple-800', icon: Sparkles, label: 'AI Generated' },
    MANAGER_ADDED: { color: 'bg-blue-100 text-blue-800', icon: Users, label: 'People Leader Added' },
    EMPLOYEE_PROPOSED: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending Acknowledgement' },
    APPROVED: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Acknowledged' },
    IN_PROGRESS: { color: 'bg-blue-100 text-blue-800', icon: TrendingUp, label: 'In Progress' },
    COMPLETED: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Completed' },
    REJECTED: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
  }

  const simpleConfig = {
    AI_GENERATED: { color: 'bg-stone-100 text-stone-700', icon: Sparkles, label: 'AI Generated' },
    MANAGER_ADDED: { color: 'bg-heritage-blue/10 text-heritage-blue', icon: Users, label: 'People Leader Added' },
    EMPLOYEE_PROPOSED: { color: 'bg-stone-100 text-stone-700', icon: Clock, label: 'Pending Acknowledgement' },
    APPROVED: { color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle2, label: 'Acknowledged' },
    IN_PROGRESS: { color: 'bg-heritage-blue/10 text-heritage-blue', icon: TrendingUp, label: 'In Progress' },
    COMPLETED: { color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle2, label: 'Completed' },
    REJECTED: { color: 'bg-stone-100 text-stone-700', icon: XCircle, label: 'Not Taken Forward' },
  }

  const config = (detailed ? detailedConfig : simpleConfig)[status]
  const Icon = config.icon

  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  )
}

interface EmployeeIDPViewProps {
  userId: string
  numericUserId?: number  // Optional numeric ID for KPI fetching (for TEST users)
  year?: number
  embedded?: boolean
  /** Suppress the internal "my Personalised Growth" gradient header — used when
      embedded in MemberDetailsDialog, which already supplies member context. */
  hideHeader?: boolean
  userRole?: 'employee' | 'manager'
  /** Read without changing: a One-Up or HR looking at someone else's plan from
      the Annual I&G Review. Only the person and their own People Leader edit
      or acknowledge it (Victor, 7 Sep 2026). */
  readOnly?: boolean
  performancePlan?: EmployeePerformancePlan | null  // Pre-loaded performance plan with KPIs
  employeeName?: string  // Employee name for manager view
  employeeRole?: string  // Employee role for manager view
}

export function EmployeeIDPView({
  userId,
  numericUserId,
  year,
  embedded = false,
  hideHeader = false,
  userRole = 'employee',
  readOnly = false,
  performancePlan,
  employeeName,
  employeeRole
}: EmployeeIDPViewProps) {
  // The plan is "Growth Plan" to the People Leader and "My Personalised
  // Growth" to the Team Member it belongs to (HR UAT list #3).
  const planName = userRole === 'manager' ? 'Growth Plan' : 'My Personalised Growth'
  const currentYear = year || new Date().getFullYear()
  const [viewMode] = useJourneyViewMode()
  const detailedView = viewMode === 'detailed'

  const [loading, setLoading] = useState(true)
  const [currentIDP, setCurrentIDP] = useState<IndividualDevelopmentPlan | null>(null)
  const [activeTab, setActiveTab] = useState<'career' | 'overview' | 'strengths' | 'development' | 'interventions'>('career')
  const [loadedPerformancePlan, setLoadedPerformancePlan] = useState<EmployeePerformancePlan | null>(null)
  
  // Dialog states
  const [strengthDialogOpen, setStrengthDialogOpen] = useState(false)
  const [developmentAreaDialogOpen, setDevelopmentAreaDialogOpen] = useState(false)
  const [interventionDialogOpen, setInterventionDialogOpen] = useState(false)
  const [careerAspirationDialogOpen, setCareerAspirationDialogOpen] = useState(false)
  
  // Edit states
  const [editingStrength, setEditingStrength] = useState<IDPStrength | null>(null)
  const [editingDevelopmentArea, setEditingDevelopmentArea] = useState<IDPDevelopmentArea | null>(null)
  const [editingIntervention, setEditingIntervention] = useState<IDPIntervention | null>(null)
  const [editingCareerAspiration, setEditingCareerAspiration] = useState<CareerAspiration | null>(null)

  // Delete confirmation states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{type: 'strength' | 'development_area' | 'intervention' | 'career_aspiration', id: string, title: string} | null>(null)

  // Manager action dialog states
  const [managerActionDialogOpen, setManagerActionDialogOpen] = useState(false)
  const [itemForManagerAction, setItemForManagerAction] = useState<{
    type: 'strength' | 'development_area' | 'intervention' | 'career_aspiration',
    id: string,
    title: string
  } | null>(null)

  // Extract unique KPAs from performance plan (filter out rejected objectives)
  const availableKPAs = useMemo(() => {
    const plan = performancePlan || loadedPerformancePlan
    if (!plan?.objectives) {
      if (import.meta.env.DEV) console.log('📊 No performance plan available for KPAs', { performancePlan, loadedPerformancePlan, userRole })
      return []
    }
    
    const kpaMap = new Map<string | number, KPAOption>()
    plan.objectives.forEach(objective => {
      // Only include KPAs from active objectives
      if (objective.status === 'active' && objective.kpa) {
        const kpaId = objective.kpa.id
        if (!kpaMap.has(kpaId)) {
          kpaMap.set(kpaId, {
            id: kpaId,
            name: objective.kpa.name,
            display_name: objective.kpa.display_name,
            weight_percentage: objective.kpa.weight_percentage
          })
        }
      }
    })
    const kpas = Array.from(kpaMap.values())
    if (import.meta.env.DEV) console.log('📊 Loaded KPAs for IDP view:', kpas.length, 'KPAs', { userRole, userId, numericUserId })
    return kpas
  }, [performancePlan, loadedPerformancePlan, userRole, userId, numericUserId])

  useEffect(() => {
    loadUserIDP()
  }, [userId, currentYear])

  useEffect(() => {
    if (!performancePlan && userRole === 'manager' && userId) {
      loadEmployeePerformancePlan()
    }
  }, [userId, userRole, performancePlan])

  const loadEmployeePerformancePlan = async () => {
    if (!userId) {
      if (import.meta.env.DEV) console.log('📊 Cannot load performance plan - missing userId', { userId })
      return
    }

    if (import.meta.env.DEV) console.log('📊 Loading employee performance plan for manager...', { userId, numericUserId, currentYear })
    try {
      // For managers viewing employee IDPs, fetch the employee's performance plan to get KPIs
      const { performanceService } = await import('@/services/api/performanceService')
      
      // Try numeric ID first if available, otherwise use string ID
      const employeeId = numericUserId || userId
      if (import.meta.env.DEV) console.log('📊 Using employee ID:', employeeId, 'type:', typeof employeeId)
      
      const plan = await performanceService.getEmployeePerformancePlan(employeeId, currentYear)
      if (import.meta.env.DEV) console.log('📊 Successfully loaded employee performance plan', plan)
      setLoadedPerformancePlan(plan)
    } catch (error) {
      console.error('❌ Failed to load employee performance plan for KPIs:', error)
      // Not critical - manager can still add items without KPI linking
    }
  }

  const loadUserIDP = async () => {
    if (!userId) return

    try {
      setLoading(true)
      const response = await idpService.getUserIDPs(userId, currentYear)
      
      if (response.idps && response.idps.length > 0) {
        // Get the most recent IDP
        const idp = response.idps[0]
        // Load full IDP with related items
        const fullIDP = await idpService.getIDP(idp.id)
        setCurrentIDP(fullIDP)
      } else {
        setCurrentIDP(null)
      }
    } catch (error) {
      console.error('Failed to load IDP:', error)
      toast.error(`Failed to load ${planName}`)
      setCurrentIDP(null)
    } finally {
      setLoading(false)
    }
  }

  const handleAddStrength = () => {
    setStrengthDialogOpen(true)
  }

  const handleAddDevelopmentArea = () => {
    setDevelopmentAreaDialogOpen(true)
  }

  const handleAddIntervention = () => {
    setInterventionDialogOpen(true)
  }

  const handleAddCareerAspiration = () => {
    setCareerAspirationDialogOpen(true)
  }

  // Submit handlers
  const handleSubmitStrength = async (data: CreateStrengthRequest) => {
    if (!currentIDP) return
    try {
      if (editingStrength) {
        // Update existing strength
        await idpService.updateStrength(currentIDP.id, editingStrength.id, resetStatusOnEmployeeEdit(data))
        toast.success('Strength updated successfully')
      } else {
        // Add new strength with appropriate status
        const strengthData = {
          ...data,
          item_status: userRole === 'manager' ? IDPItemStatus.MANAGER_ADDED : IDPItemStatus.EMPLOYEE_PROPOSED
        }
        await idpService.addStrength(currentIDP.id, strengthData)
        toast.success('Strength added successfully')
      }
      await loadUserIDP() // Reload to show changes
    } catch (error) {
      toast.error(editingStrength ? 'Failed to update strength' : 'Failed to add strength')
      throw error
    }
  }

  const handleSubmitDevelopmentArea = async (data: CreateDevelopmentAreaRequest) => {
    if (!currentIDP) return
    try {
      if (editingDevelopmentArea) {
        // Update existing development area
        await idpService.updateDevelopmentArea(currentIDP.id, editingDevelopmentArea.id, resetStatusOnEmployeeEdit(data))
        toast.success('Development area updated successfully')
      } else {
        // Add new development area with appropriate status
        const areaData = {
          ...data,
          item_status: userRole === 'manager' ? IDPItemStatus.MANAGER_ADDED : IDPItemStatus.EMPLOYEE_PROPOSED
        }
        await idpService.addDevelopmentArea(currentIDP.id, areaData)
        toast.success('Development area added successfully')
      }
      await loadUserIDP()
    } catch (error) {
      toast.error(editingDevelopmentArea ? 'Failed to update development area' : 'Failed to add development area')
      throw error
    }
  }

  const handleSubmitIntervention = async (data: CreateInterventionRequest) => {
    if (!currentIDP) return
    try {
      if (editingIntervention) {
        // Update existing intervention
        await idpService.updateIntervention(currentIDP.id, editingIntervention.id, resetStatusOnEmployeeEdit(data))
        toast.success('Intervention updated successfully')
      } else {
        // Add new intervention with appropriate status
        const interventionData = {
          ...data,
          item_status: userRole === 'manager' ? IDPItemStatus.MANAGER_ADDED : IDPItemStatus.EMPLOYEE_PROPOSED
        }
        await idpService.addIntervention(currentIDP.id, interventionData)
        toast.success('Intervention added successfully')
      }
      await loadUserIDP()
    } catch (error) {
      toast.error(editingIntervention ? 'Failed to update intervention' : 'Failed to add intervention')
      throw error
    }
  }

  const handleSubmitCareerAspiration = async (data: CreateCareerAspirationRequest) => {
    if (!currentIDP) return
    try {
      if (editingCareerAspiration) {
        // Update existing career aspiration
        await idpService.updateCareerAspiration(currentIDP.id, editingCareerAspiration.id, data)
        toast.success('Career aspiration updated successfully')
      } else {
        // Add new career aspiration with appropriate status
        const aspirationData = {
          ...data,
          item_status: userRole === 'manager' ? IDPItemStatus.MANAGER_ADDED : IDPItemStatus.EMPLOYEE_PROPOSED
        }
        await idpService.addCareerAspiration(currentIDP.id, aspirationData)
        toast.success('Career aspiration added successfully')
      }
      await loadUserIDP()
    } catch (error) {
      toast.error(editingCareerAspiration ? 'Failed to update career aspiration' : 'Failed to add career aspiration')
      throw error
    }
  }

  const handleCreateIDP = async () => {
    try {
      await idpService.createIDP(userId, {
        user_id: userId,
        performance_year: currentYear,
        performance_category: 'FULL' as PerformanceCategory,
        development_focus: 'IMPROVE_CURRENT' as DevelopmentFocus,
        // Track who initiated the IDP
        initiated_by_role: userRole,
        // If manager is creating, pass their user ID (would come from auth context)
        initiated_by_user_id: userRole === 'manager' ? undefined : undefined, // TODO: Get from auth context
      })
      toast.success(userRole === 'manager' 
        ? `Growth Plan created for ${employeeName || 'team member'}`
        : 'My Personalised Growth created successfully'
      )
      await loadUserIDP()
    } catch (error) {
      toast.error(`Failed to create ${planName}`)
      console.error(error)
    }
  }

  // Edit handlers
  const handleEditStrength = (strength: IDPStrength) => {
    setEditingStrength(strength)
    setStrengthDialogOpen(true)
  }

  const handleEditDevelopmentArea = (area: IDPDevelopmentArea) => {
    setEditingDevelopmentArea(area)
    setDevelopmentAreaDialogOpen(true)
  }

  const handleEditIntervention = (intervention: IDPIntervention) => {
    setEditingIntervention(intervention)
    setInterventionDialogOpen(true)
  }

  const handleEditCareerAspiration = (aspiration: CareerAspiration) => {
    setEditingCareerAspiration(aspiration)
    setCareerAspirationDialogOpen(true)
  }

  // Delete handlers
  const handleDeleteClick = (type: 'strength' | 'development_area' | 'intervention' | 'career_aspiration', id: string, title: string) => {
    setItemToDelete({ type, id, title })
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!itemToDelete || !currentIDP) return

    try {
      switch (itemToDelete.type) {
        case 'strength':
          await idpService.deleteStrength(currentIDP.id, itemToDelete.id)
          toast.success('Strength deleted successfully')
          break
        case 'development_area':
          await idpService.deleteDevelopmentArea(currentIDP.id, itemToDelete.id)
          toast.success('Development area deleted successfully')
          break
        case 'intervention':
          await idpService.deleteIntervention(currentIDP.id, itemToDelete.id)
          toast.success('Intervention deleted successfully')
          break
        case 'career_aspiration':
          await idpService.deleteCareerAspiration(currentIDP.id, itemToDelete.id)
          toast.success('Career aspiration deleted successfully')
          break
      }
      
      await loadUserIDP()
      setDeleteConfirmOpen(false)
      setItemToDelete(null)
    } catch (error) {
      toast.error(`Failed to delete ${itemToDelete.type.replace('_', ' ')}`)
      console.error(error)
    }
  }

  // Manager action handlers
  const handleManagerActionClick = (type: 'strength' | 'development_area' | 'intervention' | 'career_aspiration', id: string, title: string) => {
    setItemForManagerAction({ type, id, title })
    setManagerActionDialogOpen(true)
  }

  const handleApproveItem = async (notes: string) => {
    if (!itemForManagerAction || !currentIDP) return
    
    try {
      await idpService.approveIDPItem(
        currentIDP.id,
        itemForManagerAction.type,
        itemForManagerAction.id,
        notes || undefined
      )
      toast.success(`${itemForManagerAction.type.replace('_', ' ')} acknowledged successfully`)
      await loadUserIDP()
    } catch (error) {
      toast.error('Failed to acknowledge item')
      console.error(error)
      throw error
    }
  }

  const handleRejectItem = async (reason: string) => {
    if (!itemForManagerAction || !currentIDP) return
    
    try {
      await idpService.rejectIDPItem(
        currentIDP.id,
        itemForManagerAction.type,
        itemForManagerAction.id,
        reason
      )
      toast.error(`${itemForManagerAction.type.replace('_', ' ')} rejected`)
      await loadUserIDP()
    } catch (error) {
      toast.error('Failed to reject item')
      console.error(error)
      throw error
    }
  }

  // Check if user can edit/delete an item based on their role and who created it
  const canEditDeleteItem = (itemStatus: IDPItemStatus) => {
    // Reading someone else's plan (the One-Up or HR, from the review).
    if (readOnly) return false
    if (userRole === 'employee') {
      // The team member owns their growth plan: they can edit/delete any of
      // their OWN items at any time — including ones the People Leader has
      // already acknowledged (APPROVED) or AI-suggested. Only items the PL
      // authored (MANAGER_ADDED) are off-limits. Editing an acknowledged item
      // resets it to EMPLOYEE_PROPOSED (see resetStatusOnEmployeeEdit) so the
      // PL re-acknowledges the change.
      return itemStatus !== IDPItemStatus.MANAGER_ADDED
    } else if (userRole === 'manager') {
      // Managers can only edit/delete items they added
      return itemStatus === IDPItemStatus.MANAGER_ADDED
    }
    return false
  }

  // When a team member edits one of their own items, resend item_status as
  // EMPLOYEE_PROPOSED so an acknowledged item flips back to "Pending People
  // Leader Review" and the backend re-opens the (possibly finalized) plan.
  const resetStatusOnEmployeeEdit = <T,>(updates: T): T =>
    userRole === 'employee'
      ? { ...updates, item_status: IDPItemStatus.EMPLOYEE_PROPOSED }
      : updates

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-heritage-blue"></div>
      </div>
    )
  }

  // No IDP state
  if (!currentIDP) {
    return (
      <div className={embedded ? '' : 'container mx-auto py-8'}>
        <Card className="p-12 text-center">
          <div className="max-w-md mx-auto space-y-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <Target className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold">
              {!readOnly && userRole === 'manager' && employeeName 
                ? `No Growth Plan for ${employeeName} (${currentYear})`
                : `No ${planName} for ${currentYear}`
              }
            </h3>
            <p className="text-gray-600">
              {userRole === 'manager' 
                ? `A Growth Plan helps ${employeeName || 'this team member'} focus on growth areas and career aspirations. You can start theirs to get going.`
                : `My Personalised Growth helps you focus on growth areas and career aspirations. It's typically created after your 1:1 check-in dialogue.`
              }
            </p>
            <Button onClick={handleCreateIDP} size="lg" disabled={readOnly}>
              <Plus className="w-4 h-4 mr-2" />
              {userRole === 'manager' 
                ? `Create a Growth Plan for ${employeeName || 'Team Member'}`
                : 'Create My Personalised Growth'
              }
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  const strengthsCount = currentIDP.strengths?.length || 0
  const developmentCount = currentIDP.development_areas?.length || 0
  const interventionsCount = currentIDP.interventions?.length || 0
  const careerCount = currentIDP.career_aspirations?.length || 0
  const totalItems = strengthsCount + developmentCount + interventionsCount + careerCount

  // Count pending review items for managers
  const pendingReviewCount = userRole === 'manager' ? [
    ...currentIDP.strengths || [],
    ...currentIDP.development_areas || [],
    ...currentIDP.interventions || [],
    ...currentIDP.career_aspirations || []
  ].filter(item => item.item_status === IDPItemStatus.EMPLOYEE_PROPOSED).length : 0

  const isFinalized = currentIDP.is_finalized
  // Adding, editing and acknowledging are for the person and their own People
  // Leader. A finalized plan is closed to the person, but their People Leader
  // can still add to it — the review asks them to (HR process owner, 8 Sep 2026); the
  // backend re-opens the plan when they do.
  const canChange = !readOnly && (!isFinalized || userRole === 'manager')

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={embedded ? 'flex flex-col' : 'container mx-auto py-8 flex flex-col'}
    >
      {/* Header — manager context only. On the employee journey this gradient
          banner (title + "N strengths / opportunities / enablements" stats)
          duplicates the "my Personalised Growth" nav tab and the journey
          banner above it, so it's hidden for employees; they go straight to
          the release banner and their growth entries. Managers keep it for
          the member name / role / year + pending-review context. */}
      {!readOnly && userRole === 'manager' && !hideHeader && (
      <div className="bg-gradient-to-br from-heritage-blue to-future-blue text-white shadow-lg rounded-2xl">
        <div className="px-8 py-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                  <BookOpen className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">{userRole === 'manager' ? 'Growth Plan' : 'my Personalised Growth'}</h1>
                  <p className="text-blue-100 text-sm mt-1">
                    {!readOnly && userRole === 'manager' && employeeName && employeeRole 
                      ? `${employeeName} • ${employeeRole} • ${currentYear}`
                      : `${currentYear} Impact & Growth Year`
                    }
                  </p>
                </div>
              </div>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mt-4">
                {/* Strengths */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="h-4 w-4" />
                    <span className="text-xs text-blue-100">
                      Strengths
                    </span>
                  </div>
                  <p className="text-2xl font-bold">
                    {strengthsCount}
                  </p>
                </div>
                
                {/* Development Areas */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-xs text-blue-100">
                      Growth Opportunities
                    </span>
                  </div>
                  <p className="text-2xl font-bold">
                    {developmentCount}
                  </p>
                </div>
                
                {/* Interventions */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="flex items-center gap-2 mb-1">
                    <BookOpen className="h-4 w-4" />
                    <span className="text-xs text-blue-100">
                      Growth Enablements
                    </span>
                  </div>
                  <p className="text-2xl font-bold">
                    {interventionsCount}
                  </p>
                </div>
              </div>

              {/* Active Indicators */}
              <div className="flex items-center gap-4 mt-4 text-sm">
                {!readOnly && userRole === 'manager' && pendingReviewCount > 0 && (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-orange-300" />
                    <span className="text-sm text-orange-100">
                      {pendingReviewCount} item(s) pending review
                    </span>
                  </div>
                )}
                
                {isFinalized && (
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-300" />
                    <span className="text-sm text-green-100">
                      {planName} finalized{!readOnly && userRole === 'manager' ? ' — you can still add to it' : ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* IDP release/withdraw UI removed — Personalised Growth is mutually
          visible to the team member and their People Leader by default (no
          release gate). Release was only ever appropriate for per-entry
          Impact Portfolio items, not the whole growth plan. */}

      {/* Main Content */}
      <div className="bg-gray-50 rounded-2xl mt-4 min-w-0 max-w-full">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(value: any) => setActiveTab(value)} className="flex flex-col min-w-0 max-w-full">
          {/* Sub-tab strip — unified with the EmployeeKPAView reference
              pattern: flex-1 + min-w-[160px] tabs fill the row at desktop and
              wrap evenly when narrow (768 / 375). Each tab = lucide icon +
              sentence-case label, with a filled heritage-blue active pill.
              Count badges "(n)" append to the label. Identical in Simple /
              Detailed (no per-mode metric on these sub-tabs). */}
          <TabsList className="flex flex-wrap justify-start items-stretch gap-1 w-full min-w-0 max-w-full rounded-none border-b border-gray-200 h-auto p-1.5 bg-white">
            <TabsTrigger
              value="career"
              className="gap-2 h-auto min-h-14 px-3 py-2 flex-1 min-w-[160px] justify-center rounded-lg text-gray-600 data-[state=active]:bg-heritage-blue data-[state=active]:text-white"
            >
              <div className="flex items-center gap-1.5">
                <Compass className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">Growth Focus</span>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="strengths"
              className="gap-2 h-auto min-h-14 px-3 py-2 flex-1 min-w-[160px] justify-center rounded-lg text-gray-600 data-[state=active]:bg-heritage-blue data-[state=active]:text-white"
            >
              <div className="flex items-center gap-1.5">
                <Award className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">
                  Key strengths{strengthsCount > 0 && ` (${strengthsCount})`}
                </span>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="development"
              className="gap-2 h-auto min-h-14 px-3 py-2 flex-1 min-w-[160px] justify-center rounded-lg text-gray-600 data-[state=active]:bg-heritage-blue data-[state=active]:text-white"
            >
              <div className="flex items-center gap-1.5">
                <TrendingUp className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">
                  Growth opportunities{developmentCount > 0 && ` (${developmentCount})`}
                </span>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="interventions"
              className="gap-2 h-auto min-h-14 px-3 py-2 flex-1 min-w-[160px] justify-center rounded-lg text-gray-600 data-[state=active]:bg-heritage-blue data-[state=active]:text-white"
            >
              <div className="flex items-center gap-1.5">
                <BookOpen className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">
                  Growth enablements{interventionsCount > 0 && ` (${interventionsCount})`}
                </span>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="overview"
              className="gap-2 h-auto min-h-14 px-3 py-2 flex-1 min-w-[160px] justify-center rounded-lg text-gray-600 data-[state=active]:bg-heritage-blue data-[state=active]:text-white"
            >
              <div className="flex items-center gap-1.5">
                <Target className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">Overview</span>
              </div>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-4">
            {/* Progress Summary Cards */}
            {(() => {
              const allItems = [
                ...(currentIDP.strengths || []),
                ...(currentIDP.development_areas || []),
                ...(currentIDP.interventions || []),
                ...(currentIDP.career_aspirations || [])
              ]
              const approvedItems = allItems.filter(i => i.item_status === 'APPROVED' || i.item_status === 'MANAGER_ADDED')
              const pendingItems = allItems.filter(i => i.item_status === 'EMPLOYEE_PROPOSED')
              const progressPercent = totalItems > 0 ? Math.round((approvedItems.length / totalItems) * 100) : 0
              
              return (
                <>
                  {/* Career Aspirations Summary */}
                  {currentIDP.career_aspirations && currentIDP.career_aspirations.length > 0 && (
                    <Card className="p-5 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border-indigo-200">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <Compass className="w-6 h-6 text-indigo-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-indigo-900 mb-1">Career Aspirations</h4>
                          <p className="text-indigo-700">{currentIDP.career_aspirations[0].title}</p>
                          {currentIDP.career_aspirations[0].description && (
                            <p className="text-sm text-indigo-600 mt-2">{currentIDP.career_aspirations[0].description}</p>
                          )}
                        </div>
                        <StatusBadge status={currentIDP.career_aspirations[0].item_status} proposedBy={currentIDP.career_aspirations[0].proposed_by} />
                      </div>
                    </Card>
                  )}

                  {/* Development Plan Details - Full Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Key Strengths */}
                    <Card className="p-5 border-l-4 border-l-purple-500">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <Award className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-purple-900">Key Strengths</h5>
                          <p className="text-xs text-gray-500">{strengthsCount} identified</p>
                        </div>
                      </div>
                      {currentIDP.strengths && currentIDP.strengths.length > 0 ? (
                        <ul className="space-y-2">
                          {currentIDP.strengths.map(s => (
                            <li key={s.id} className="flex items-start gap-2 p-2 rounded-lg bg-purple-50/50 hover:bg-purple-50 transition-colors">
                              <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${s.item_status === 'APPROVED' || s.item_status === 'MANAGER_ADDED' ? 'bg-emerald-500' : detailedView ? 'bg-amber-500' : 'bg-stone-400'}`} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-800">{s.title}</p>
                                {s.description && <p className="text-xs text-gray-500 truncate">{s.description}</p>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400 text-center py-4">No strengths identified yet</p>
                      )}
                    </Card>

                    {/* Growth Opportunities */}
                    <Card className="p-5 border-l-4 border-l-blue-500">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-blue-900">Growth Opportunities</h5>
                          <p className="text-xs text-gray-500">{developmentCount} areas for development</p>
                        </div>
                      </div>
                      {currentIDP.development_areas && currentIDP.development_areas.length > 0 ? (
                        <ul className="space-y-2">
                          {currentIDP.development_areas.map(d => (
                            <li key={d.id} className="flex items-start gap-2 p-2 rounded-lg bg-blue-50/50 hover:bg-blue-50 transition-colors">
                              <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${d.item_status === 'APPROVED' || d.item_status === 'MANAGER_ADDED' ? 'bg-emerald-500' : detailedView ? 'bg-amber-500' : 'bg-stone-400'}`} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-800">{d.title}</p>
                                {d.current_state && <p className="text-xs text-gray-500 truncate">{d.current_state}</p>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400 text-center py-4">No growth opportunities identified yet</p>
                      )}
                    </Card>

                    {/* Growth Enablements */}
                    <Card className="p-5 border-l-4 border-l-green-500">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <BookOpen className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-green-900">Growth Enablements</h5>
                          <p className="text-xs text-gray-500">{interventionsCount} planned activities</p>
                        </div>
                      </div>
                      {currentIDP.interventions && currentIDP.interventions.length > 0 ? (
                        <ul className="space-y-2">
                          {currentIDP.interventions.map(i => (
                            <li key={i.id} className="flex items-start gap-2 p-2 rounded-lg bg-green-50/50 hover:bg-green-50 transition-colors">
                              <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${i.item_status === 'APPROVED' || i.item_status === 'MANAGER_ADDED' ? 'bg-emerald-500' : detailedView ? 'bg-amber-500' : 'bg-stone-400'}`} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-800">{i.title}</p>
                                <p className="text-xs text-gray-500">
                                  {(i.enablement_types && i.enablement_types.length > 0
                                    ? i.enablement_types
                                    : i.intervention_type ? [i.intervention_type] : []
                                  ).map(t => t.replace(/_/g, ' ')).join(', ')}
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400 text-center py-4">No interventions planned yet</p>
                      )}
                    </Card>

                    {/* Status Legend */}
                    <Card className="p-5 border-l-4 border-l-gray-300">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <CheckCircle2 className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Acknowledgement Status</h5>
                          <p className="text-xs text-gray-500">Track your progress</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-2 rounded-lg bg-emerald-50">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="text-sm text-emerald-800">Acknowledged</span>
                          </div>
                          <span className="text-sm font-semibold text-emerald-900">{approvedItems.length}</span>
                        </div>
                        <div className={`flex items-center justify-between p-2 rounded-lg ${detailedView ? 'bg-amber-50' : 'bg-stone-100'}`}>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${detailedView ? 'bg-amber-500' : 'bg-stone-400'}`} />
                            <span className={`text-sm ${detailedView ? 'text-amber-800' : 'text-stone-700'}`}>Pending Review</span>
                          </div>
                          <span className={`text-sm font-semibold ${detailedView ? 'text-amber-900' : 'text-stone-800'}`}>{pendingItems.length}</span>
                        </div>
                        <div className="pt-2 border-t">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Total Items</span>
                            <span className="font-semibold">{totalItems}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>

                  {totalItems === 0 && (
                    <Card className="p-8">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                          <Compass className="w-8 h-8 text-gray-400" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-700 mb-2">
                          {userRole === 'manager' ? 'Start Building Their Growth Plan' : 'Start Building My Personalised Growth'}
                        </h4>
                        <p className="text-gray-500 mb-4">
                          {userRole === 'manager'
                            ? 'Add their career objective, strengths, and development areas to build out the plan.'
                            : 'Add your career objective, strengths, and development areas to build out your plan.'}
                        </p>
                        <Button onClick={() => setActiveTab('career')} variant="outline">
                          Set Career Aspirations
                        </Button>
                      </div>
                    </Card>
                  )}
                </>
              )
            })()}
          </TabsContent>

          {/* Strengths Tab */}
          <TabsContent value="strengths" className="space-y-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Key Strengths</h3>
              {canChange && (
                <Button onClick={handleAddStrength} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Key Strength
                </Button>
              )}
            </div>

            {strengthsCount === 0 ? (
              <Card className="p-8 text-center">
                <Award className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No key strengths added yet</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {currentIDP.strengths?.map((strength) => (
                  <Card key={strength.id} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <h4 className="text-lg font-semibold flex-1">{strength.title}</h4>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={strength.item_status} proposedBy={strength.proposed_by} />
                        {canEditDeleteItem(strength.item_status) && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditStrength(strength)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick('strength', strength.id, strength.title)}
                              className="h-8 w-8 p-0 hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        {!readOnly && userRole === 'manager' && strength.item_status === IDPItemStatus.EMPLOYEE_PROPOSED && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleManagerActionClick('strength', strength.id, strength.title)}
                              className="h-8 px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-4">{strength.description}</p>

                    {/* Linked KPAs - only show if there are valid linked KPAs */}
                    {(() => {
                      const validKPAs = strength.linked_kpa_ids?.filter((kpaId: string) => 
                        availableKPAs.some(k => String(k.id) === kpaId)
                      ) || []
                      return validKPAs.length > 0 ? (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Linked KPAs</p>
                          <div className="flex flex-wrap gap-2">
                            {validKPAs.map((kpaId: string) => {
                              const kpa = availableKPAs.find(k => String(k.id) === kpaId)!
                              return (
                                <Badge key={kpaId} variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-900">
                                  <span className="text-xs font-medium">{kpa.display_name || kpa.name}</span>
                                </Badge>
                              )
                            })}
                          </div>
                        </div>
                      ) : null
                    })()}

                    {strength.employee_notes && (
                      <div className="bg-blue-50 p-3 rounded-lg mb-3">
                        <p className="text-sm font-medium text-blue-900 mb-1">my Notes</p>
                        <p className="text-sm text-blue-800">{strength.employee_notes}</p>
                      </div>
                    )}

                    {strength.manager_notes && (
                      <div className="bg-green-50 p-3 rounded-lg mb-3">
                        <p className="text-sm font-medium text-green-900 mb-1">People Leader Notes</p>
                        <p className="text-sm text-green-800">{strength.manager_notes}</p>
                      </div>
                    )}

                    {strength.item_status === 'REJECTED' && strength.rejection_reason && (
                      <div className="bg-red-50 border border-red-200 p-3 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-900 mb-1">Rejection Reason</p>
                          <p className="text-sm text-red-800">{strength.rejection_reason}</p>
                        </div>
                      </div>
                    )}

                    {strength.approved_at && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 mt-3">
                        <Calendar className="w-4 h-4" />
                        <span>Acknowledged on {new Date(strength.approved_at).toLocaleDateString()}</span>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Development Areas Tab */}
          <TabsContent value="development" className="space-y-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Growth Opportunities</h3>
              {canChange && (
                <Button onClick={handleAddDevelopmentArea} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Growth Opportunity
                </Button>
              )}
            </div>

            {developmentCount === 0 ? (
              <Card className="p-8 text-center">
                <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No growth opportunities added yet</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {currentIDP.development_areas?.map((area) => (
                  <Card key={area.id} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-lg font-semibold">{area.title}</h4>
                          {area.priority && (
                            <Badge variant={area.priority === 1 ? 'destructive' : area.priority === 2 ? 'default' : 'secondary'}>
                              {area.priority === 1 ? 'High' : area.priority === 2 ? 'Medium' : 'Low'} Priority
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={area.item_status} proposedBy={area.proposed_by} />
                        {canEditDeleteItem(area.item_status) && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditDevelopmentArea(area)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick('development_area', area.id, area.title)}
                              className="h-8 w-8 p-0 hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        {!readOnly && userRole === 'manager' && area.item_status === IDPItemStatus.EMPLOYEE_PROPOSED && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleManagerActionClick('development_area', area.id, area.title)}
                              className="h-8 px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-4">{area.description}</p>

                    {/* Linked KPAs - only show if there are valid linked KPAs */}
                    {(() => {
                      const validKPAs = area.linked_kpa_ids?.filter((kpaId: string) => 
                        availableKPAs.some(k => String(k.id) === kpaId)
                      ) || []
                      return validKPAs.length > 0 ? (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Linked KPAs</p>
                          <div className="flex flex-wrap gap-2">
                            {validKPAs.map((kpaId: string) => {
                              const kpa = availableKPAs.find(k => String(k.id) === kpaId)!
                              return (
                                <Badge key={kpaId} variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-900">
                                  <span className="text-xs font-medium">{kpa.display_name || kpa.name}</span>
                                </Badge>
                              )
                            })}
                          </div>
                        </div>
                      ) : null
                    })()}

                    {(area.current_state || area.desired_state) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        {area.current_state && (
                          <div className="bg-orange-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-orange-900 mb-1">Current State</p>
                            <p className="text-sm text-orange-800">{area.current_state}</p>
                          </div>
                        )}
                        {area.desired_state && (
                          <div className="bg-green-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-green-900 mb-1">Desired State</p>
                            <p className="text-sm text-green-800">{area.desired_state}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {area.area_type && (
                      <Badge variant="outline" className="mb-3">
                        {area.area_type.replace(/_/g, ' ')}
                      </Badge>
                    )}

                    {area.manager_notes && (
                      <div className="bg-blue-50 p-3 rounded-lg mb-3">
                        <p className="text-sm font-medium text-blue-900 mb-1">People Leader Notes</p>
                        <p className="text-sm text-blue-800">{area.manager_notes}</p>
                      </div>
                    )}

                    {area.item_status === 'REJECTED' && area.rejection_reason && (
                      <div className="bg-red-50 border border-red-200 p-3 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-900 mb-1">Rejection Reason</p>
                          <p className="text-sm text-red-800">{area.rejection_reason}</p>
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Interventions Tab */}
          <TabsContent value="interventions" className="space-y-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Growth Enablements</h3>
              {canChange && (
                <Button onClick={handleAddIntervention} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Growth Enablement
                </Button>
              )}
            </div>

            {interventionsCount === 0 ? (
              <Card className="p-8 text-center">
                <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No growth enablements added yet</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {currentIDP.interventions?.map((intervention) => (
                  <Card key={intervention.id} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <h4 className="text-lg font-semibold flex-1">{intervention.title}</h4>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={intervention.item_status} proposedBy={intervention.proposed_by} />
                        {canEditDeleteItem(intervention.item_status) && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditIntervention(intervention)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick('intervention', intervention.id, intervention.title)}
                              className="h-8 w-8 p-0 hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        {!readOnly && userRole === 'manager' && intervention.item_status === IDPItemStatus.EMPLOYEE_PROPOSED && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleManagerActionClick('intervention', intervention.id, intervention.title)}
                              className="h-8 px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-4">{intervention.description}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      {intervention.time_frame && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>Time Frame: {intervention.time_frame}</span>
                        </div>
                      )}
                      {intervention.target_completion_date && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>Target: {new Date(intervention.target_completion_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>

                    {(() => {
                      const types = (intervention.enablement_types && intervention.enablement_types.length > 0)
                        ? intervention.enablement_types
                        : intervention.intervention_type ? [intervention.intervention_type] : []
                      return types.length > 0 ? (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {types.map((t) => (
                            <Badge key={t} variant="outline">
                              {t.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      ) : null
                    })()}

                    {intervention.employee_feedback && (
                      <div className="bg-blue-50 p-3 rounded-lg mb-3">
                        <p className="text-sm font-medium text-blue-900 mb-1">Team Member Feedback</p>
                        <p className="text-sm text-blue-800">{intervention.employee_feedback}</p>
                      </div>
                    )}

                    {intervention.item_status === 'REJECTED' && intervention.rejection_reason && (
                      <div className="bg-red-50 border border-red-200 p-3 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-900 mb-1">Rejection Reason</p>
                          <p className="text-sm text-red-800">{intervention.rejection_reason}</p>
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Career Goal Tab */}
          <TabsContent value="career" className="space-y-4 mt-4">
            {/* Performance History Trend - shows F&O legacy + current system ratings */}
            {/* userId IS the F&O personnel number (e.g., 43xxxxx), use it directly for F&O lookups */}
            <PerformanceHistoryTrend
              userId={userId}
              employeeId={userId}
              currentYear={currentYear}
            />
            
            <CareerGoalSection
              careerAspiration={currentIDP.career_aspirations?.[0] || null}
              onSave={async (data) => {
                await idpService.addCareerAspiration(currentIDP.id, data)
                await loadUserIDP()
              }}
              onUpdate={async (aspirationId, data) => {
                await idpService.updateCareerAspiration(currentIDP.id, aspirationId, resetStatusOnEmployeeEdit(data))
                await loadUserIDP()
              }}
              onDelete={async (aspirationId) => {
                await idpService.deleteCareerAspiration(currentIDP.id, aspirationId)
                await loadUserIDP()
              }}
              onManagerAction={
                readOnly ? undefined : (aspirationId, title) => handleManagerActionClick('career_aspiration', aspirationId, title)
              }
              isFinalized={readOnly || (isFinalized && userRole !== 'manager')}
              userRole={userRole}
            />

            {/* The People Leader's view — what the Annual I&G Review reads (HR process owner, 8 Sep 2026) */}
            <LeaderGrowthFocus plan={currentIDP} canEdit={canChange && userRole === 'manager'} onSaved={loadUserIDP} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add/Edit Dialogs */}
      {currentIDP && (
        <>
          <StrengthDialog
            open={strengthDialogOpen}
            onOpenChange={(open) => {
              setStrengthDialogOpen(open)
              if (!open) setEditingStrength(null) // Clear editing state on close
            }}
            onSubmit={handleSubmitStrength}
            idpId={currentIDP.id}
            userId={numericUserId || userId}  // Use numeric ID if available
            year={currentYear}
            editingStrength={editingStrength}
            kpas={availableKPAs}  // Pass KPAs from user's objectives
          />
          <AddDevelopmentAreaDialog
            open={developmentAreaDialogOpen}
            onOpenChange={(open) => {
              setDevelopmentAreaDialogOpen(open)
              if (!open) setEditingDevelopmentArea(null) // Clear editing state on close
            }}
            onSubmit={handleSubmitDevelopmentArea}
            idpId={currentIDP.id}
            userId={numericUserId || userId}  // Use numeric ID if available
            year={currentYear}
            editingDevelopmentArea={editingDevelopmentArea}
            kpas={availableKPAs}  // Pass KPAs from user's objectives
          />
          <AddInterventionDialog
            open={interventionDialogOpen}
            onOpenChange={(open) => {
              setInterventionDialogOpen(open)
              if (!open) setEditingIntervention(null) // Clear editing state on close
            }}
            onSubmit={handleSubmitIntervention}
            idpId={currentIDP.id}
            editingIntervention={editingIntervention}
          />
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Item"
        message={`Are you sure you want to delete "${itemToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
      />

      {/* Manager Action Dialog */}
      {itemForManagerAction && (
        <ManagerActionDialog
          open={managerActionDialogOpen}
          onOpenChange={setManagerActionDialogOpen}
          onApprove={handleApproveItem}
          onReject={handleRejectItem}
          itemTitle={itemForManagerAction.title}
          itemType={itemForManagerAction.type.replace('_', ' ')}
        />
      )}
    </motion.div>
  )
}
